import { useCallback, useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	CancelError,
	CancellablePromise,
	createClient,
	fetchTeamsBulk,
	getLeapQuery,
	type HydratedJiraIssue,
	type ProductCount,
	resultsCountQuery,
	type SearchPageEdge,
	type SearchPageResponse,
	type SearchPageResults,
	type TeamDetails,
} from '@atlassian/search-client';

import {
	getDefaultEntityAti,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../common/constants/entities';
import {
	is3pProductKey,
	PrimaryProductKeys,
	ProductKeys,
	productsSupportingEmptyQuery,
} from '../../common/constants/products';
import { useAppContext } from '../../common/ui/app-context';
import { searchResultsLoadUFOExperience } from '../../common/ui/ufo/experiences';
import { getSessionIdFromLocalStorage } from '../../common/utils/events/utils';
import { serveFederatedSlackExperiment } from '../../common/utils/experiments/experiment-utils';
import { EmptyEntitiesError, ResultsFetchError } from '../../common/utils/fetch-error';
import { extractIssueAndTeamsIds, getProductResultCount } from '../../common/utils/results';
import { useSafeIntercom } from '../../common/utils/use-safe-intercom';
import { useLogException } from '../error-boundary/utils';
import { useSelectedFilters } from '../filters/selected';
import { useApplicationMode, useBackendExperiment, useBootstrap, useFrontendExperiments, useLinkedCloudARIs, useSearchSessionId } from '../store/bootstrap';
import { useSelectedInputFilters } from '../store/dropdown';
import { useAutoCorrection, useCQL, useFetchFromCursor, useIsSearchReady, useLeapQueryResult, useOriginalQuery, useProductResultCountMap, useResults, useResultsSort, useRetryCount, useSearchActions, useSearchQuery, useSelectedProduct } from '../store/search';
import { transformSearchPageConfluenceResults } from '../utils/transform-fetch-results';

import { fetchJiraFields, fetchResults } from './fetch-results';
import { getJiraFiltersForResultsCountQuery } from './utils';

export const useFetchAutoCorrection = (): void => {
	const [{ cloudId = '', aggregatorHostName }] = useBootstrap();
	const [searchQuery] = useSearchQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [, { setAutoCorrection }] = useAutoCorrection();

	useEffect(() => {
		const searchClient = createClient({
			cloudId,
			aggregatorHostName,
		});
		const containsSearchQuery = searchQuery !== '';
		const shouldGetAutoCorrection = containsSearchQuery;

		if (!shouldGetAutoCorrection) {
			return;
		}

		searchClient.getAutoCorrection({ query: searchQuery }).then((autoCorrection) => {
			setAutoCorrection(autoCorrection);
		});
	}, [searchQuery, cloudId, setAutoCorrection, aggregatorHostName]);
};

export const useFetchResultsProps = () => {
	const [{ cloudId = '', aggAbsoluteUrl, primaryProduct, thirdPartyConfigs, products }] =
		useBootstrap();
	const [searchSessionId] = useSearchSessionId();
	const [{ order, field }] = useResultsSort();
	const [selectedProduct] = useSelectedProduct();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const fetchFromCursor = useFetchFromCursor();
	const {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entities,
		hasFiltersSelected,
		entitiesForResultsCountQuery,
	} = useSelectedFilters({});
	const cql = useCQL();
	const [searchQuery] = useSearchQuery();
	const [originalQuery] = useOriginalQuery();
	const [backendExperiment] = useBackendExperiment({
		entities,
		thirdPartyFilters: selectedFilters.thirdPartyFilters,
	});
	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};
	const [frontendExperiments] = useFrontendExperiments();
	const [selectedInputFilters] = useSelectedInputFilters();
	const applicationMode = useApplicationMode();
	const [linkedCloudARIs] = useLinkedCloudARIs();
	const sessionId = getSessionIdFromLocalStorage();

	const is3pGroupingEnabled = fg('fps_3p_grouping_rollout_gate');
	serveFederatedSlackExperiment(selectedProduct, undefined, { fireExperimentExposure: true }, thirdPartyConfigs);
	const fanoutExperimentCohort = (() => {
		if (is3pGroupingEnabled === false) {
			return !selectedProduct ? 'boosted-and-smartlinks' : undefined;
		} else {
			return 'enable-v2';
		}
	})();

	// We compare these via deep value equality, as currently the objects are regenerated
	// The bug this fixes is that when adding a filter from a dropdown, we shouldn't cause a search to run or load
	// TODO refactor this so we can go back to Object.is dependency equality on these objects
	const selectedFiltersValueEquality = JSON.stringify(selectedFilters);
	const selectedEntitiesValueEquality = JSON.stringify(selectedEntities);
	const entitiesValueEquality = JSON.stringify(entities);
	const frontendExperimentsValueEquality = JSON.stringify(frontendExperiments);
	const experimentLayersValueEquality = JSON.stringify(experimentLayers);
	const unselectedProductsThirdPartyFiltersValueEquality = JSON.stringify(
		unselectedProductsThirdPartyFilters,
	);
	const entitiesForResultsCountQueryValueEquality = JSON.stringify(entitiesForResultsCountQuery);
	const productsValueEquality = JSON.stringify(products);

	return useMemo(
		() => ({
			cql,
			cloudId,
			aggAbsoluteUrl,
			primaryProduct,
			thirdPartyConfigs,
			searchSessionId,
			sessionId,
			order,
			field,
			selectedProduct,
			fetchFromCursor,
			selectedFilters,
			selectedEntities,
			entities,
			unselectedProductsThirdPartyFilters,
			entitiesForResultsCountQuery,
			hasFiltersSelected,
			searchQuery,
			originalQuery,
			experimentId,
			shadowExperimentId,
			experimentLayers,
			frontendExperiments,
			selectedInputFilters,
			applicationMode,
			linkedCloudARIs,
			products,
			fanoutExperimentCohort,
		}),
		// Note! Dependencies ending with ValueEquality are used to compare the values of the objects
		// and not the references. This is because the objects are regenerated on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			cql,
			cloudId,
			aggAbsoluteUrl,
			primaryProduct,
			thirdPartyConfigs,
			searchSessionId,
			sessionId,
			order,
			field,
			selectedProduct,
			fetchFromCursor,
			selectedFiltersValueEquality, // selectedFilters
			selectedEntitiesValueEquality, // selectedEntities
			entitiesValueEquality, // entities
			unselectedProductsThirdPartyFiltersValueEquality, // unselectedProductsThirdPartyFilters
			entitiesForResultsCountQueryValueEquality, // entitiesForResultsCountQuery
			hasFiltersSelected,
			searchQuery,
			originalQuery,
			experimentId,
			shadowExperimentId,
			experimentLayersValueEquality, // experimentLayers
			frontendExperimentsValueEquality, // frontendExperiments
			selectedInputFilters,
			applicationMode,
			linkedCloudARIs,
			productsValueEquality, // products
			fanoutExperimentCohort,
		],
	);
};

/**
 * resultsRef is a CancellablePromise that is used to cancel the inflight request if there is one.
 */
export const resultsRef: { current: CancellablePromise<SearchPageResponse> | undefined } = {
	current: undefined,
};

export type FetchSearchResultsProps = ReturnType<typeof useFetchResultsProps> & {
	fetchFromCursor?: string;
	beforeFetch?: () => void;
};

/**
 * Fetches search results using search parameters from the query
 * and filter stores. During and after requests, the results store is
 * updated accordingly. If multiple requests are fired only the last
 * request will cause the results store to be updated.
 */
export const fetchSearchResults = ({
	cql,
	searchQuery,
	selectedProduct,
	hasFiltersSelected,
	originalQuery,
	entities,
	selectedFilters,
	aggAbsoluteUrl,
	cloudId,
	searchSessionId,
	sessionId,
	primaryProduct,
	order,
	field,
	experimentId,
	shadowExperimentId,
	experimentLayers,
	fetchFromCursor,
	linkedCloudARIs,
	beforeFetch,
}: FetchSearchResultsProps): Promise<SearchPageResponse | undefined> => {
	// Cancel the inflight request if there is one
	if (resultsRef.current) {
		resultsRef.current.cancel();
	}

	// Reset results when...
	// 1. There is no search query
	// 2. There is no selected product or the selected product is not Confluence
	// 3. There are no selected child filters
	const containsSearchQuery = searchQuery !== '';
	const confluenceIsSelected = selectedProduct === 'confluence';
	const shouldTriggerNonCQLSearch =
		containsSearchQuery || (containsSearchQuery && hasFiltersSelected) || confluenceIsSelected;

	// Reset results and cancel inflight requests if we are not searching
	if (!shouldTriggerNonCQLSearch && !cql) {
		// Reset results
		return Promise.resolve(undefined);
	}

	// Run fetching results if exists
	beforeFetch?.();

	resultsRef.current = new CancellablePromise(
		fetchResults({
			cql: shouldTriggerNonCQLSearch ? undefined : cql,
			query: searchQuery,
			originalQuery,
			entities,
			selectedFilters,
			aggAbsoluteUrl,
			cloudId,
			searchSessionId,
			sessionId,
			sort: { order, field },
			sourceProduct: primaryProduct,
			experimentId,
			shadowExperimentId,
			experimentLayers,
			after: fetchFromCursor,
			limit:
				entities.length === getDefaultEntityAti().length
					? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
					: RESULT_LIMIT_FOR_SINGLE_PRODUCT,
			// TODO: This is a temporary variable to rollout highlighting to all users
			// with this variable, we can rollout frontend changes and apply backend highlighting atomically
			// and without affecting existing users
			enableHighlighting: true,
			skip3pEntityHydration: !!fg('exclude_3p_entity_in_search_results'),
			includeSpaceSubtype: !!fg('is_jsm_dc_enabled_in_ui'),
			linkedCloudARIs,
			selectedProduct,
		}),
	);

	return resultsRef.current.promise();
};

/**
 * resultsRef is a CancellablePromise that is used to cancel the inflight request if there is one.
 */
export const resultsWithCountRef: {
	current: CancellablePromise<[SearchPageResponse, SearchPageResponse]> | undefined;
} = {
	current: undefined,
};

/**
 * Fetches search results with count using search parameters from the query
 */
export const fetchSearchResultsWithCount = (
	props: FetchSearchResultsProps,
): Promise<[SearchPageResponse | undefined, SearchPageResponse | undefined]> => {
	const {
		cql,
		searchQuery,
		selectedProduct,
		hasFiltersSelected,
		originalQuery,
		entities,
		selectedFilters,
		aggAbsoluteUrl,
		cloudId,
		searchSessionId,
		sessionId,
		primaryProduct,
		order,
		field,
		experimentId,
		shadowExperimentId,
		experimentLayers,
		fetchFromCursor,
		linkedCloudARIs,
		fanoutExperimentCohort,
		beforeFetch,
	} = props;

	// Cancel the inflight request if there is one
	if (resultsWithCountRef.current) {
		resultsWithCountRef.current.cancel();
	}

	// Reset results when...
	// 1. There is no search query
	// 2. There is no selected product or the selected product is not Confluence
	// 3. There are no selected child filters
	const containsSearchQuery = searchQuery !== '';
	const confluenceIsSelected = selectedProduct === 'confluence';
	const shouldTriggerNonCQLSearch =
		containsSearchQuery || (containsSearchQuery && hasFiltersSelected) || confluenceIsSelected;

	// Reset results and cancel inflight requests if we are not searching
	if (!shouldTriggerNonCQLSearch && !cql) {
		// Reset results
		return Promise.resolve([undefined, undefined]);
	}

	// Run fetching results if exists
	beforeFetch?.();

	resultsWithCountRef.current = new CancellablePromise(
		Promise.all([
			fetchResults({
				cql: shouldTriggerNonCQLSearch ? undefined : cql,
				query: searchQuery,
				originalQuery,
				entities,
				selectedFilters,
				aggAbsoluteUrl,
				cloudId,
				searchSessionId,
				sessionId,
				sort: { order, field },
				sourceProduct: primaryProduct,
				experimentId,
				shadowExperimentId,
				experimentLayers,
				after: fetchFromCursor,
				limit:
					entities.length === getDefaultEntityAti().length
						? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
						: RESULT_LIMIT_FOR_SINGLE_PRODUCT,
				// TODO: This is a temporary variable to rollout highlighting to all users
				// with this variable, we can rollout frontend changes and apply backend highlighting atomically
				// and without affecting existing users
				enableHighlighting: true,
				skip3pEntityHydration: !!fg('exclude_3p_entity_in_search_results'),
				includeSpaceSubtype: !!fg('is_jsm_dc_enabled_in_ui'),
				linkedCloudARIs,
				selectedProduct,
				fanoutExperimentCohort,
			}),
			fetchSearchResultsCount(props),
		]),
	);

	return resultsWithCountRef.current.promise();
};

export type FetchSearchResultsCountProps = Pick<
	FetchSearchResultsProps,
	| 'selectedFilters'
	| 'searchQuery'
	| 'cloudId'
	| 'unselectedProductsThirdPartyFilters'
	| 'entitiesForResultsCountQuery'
	| 'searchSessionId'
	| 'primaryProduct'
	| 'aggAbsoluteUrl'
	| 'originalQuery'
	| 'products'
>;

export const fetchSearchResultsCount = ({
	selectedFilters,
	searchQuery,
	cloudId,
	unselectedProductsThirdPartyFilters,
	entitiesForResultsCountQuery,
	searchSessionId,
	primaryProduct,
	aggAbsoluteUrl,
	originalQuery,
	products,
}: FetchSearchResultsCountProps): Promise<SearchPageResponse> => {
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
	} = selectedFilters;
	const isEmptyQuery = !searchQuery || searchQuery.trim() === '';

	const availableProducts = products.filter(
		// If the query is empty, only some products are valid
		(product) => !isEmptyQuery || productsSupportingEmptyQuery.includes(product),
	);

	// Rovo being rolled out for townsquare, mercury
	// and all of them using confluence ARI, we keep using confluence ARI for them.
	// However, for jira, we use jira ARI as there was a known issue when using confluence ARI, it applied Confluence ip restrictions to Jira.
	const cloudIdARI =
		primaryProduct === PrimaryProductKeys.Jira && fg('jira_site_ari_query_enabled')
			? `ari:cloud:jira::site/${cloudId}`
			: `ari:cloud:confluence::site/${cloudId}`;

	const resultsCountVariables = {
		query: searchQuery,
		queryVersion: 1,
		cloudIdARI,
		limit: 0,
		commonFilters,
		confluenceFilters,
		jiraFilters: getJiraFiltersForResultsCountQuery(jiraFilters, availableProducts),
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: unselectedProductsThirdPartyFilters,
		entities: entitiesForResultsCountQuery,
		searchSessionId,
		sort: [],
		sourceProduct: primaryProduct,
		assetsFilters,
	};

	return resultsCountQuery({
		variables: resultsCountVariables,
		aggAbsoluteUrl,
		originalQuery,
	});
};

export type SearchResultsPostActionLifecycle<T> = {
	complete: (data: T) => void;
	before?: () => void;
	finally?: () => void;
};

type FetchSearchResultsPostActionsProps = Pick<
	FetchSearchResultsProps,
	'cloudId' | 'aggAbsoluteUrl'
> & {
	results?: SearchPageResults;
	jiraFields?: SearchResultsPostActionLifecycle<HydratedJiraIssue[]>;
	teamsBulk?: SearchResultsPostActionLifecycle<TeamDetails[]>;
	logException?: (error: any) => Promise<void>;
};

export const fetchSearchResultsPostActions = ({
	results,
	cloudId,
	aggAbsoluteUrl,
	jiraFields,
	teamsBulk,
	logException,
}: FetchSearchResultsPostActionsProps): void => {
	const { issueKeys, teamIds } = extractIssueAndTeamsIds(results?.edges);
	// Fetch extra fields for Issues
	if (issueKeys.length > 0) {
		jiraFields?.before?.();
		fetchJiraFields({ issueKeys, cloudId, aggAbsoluteUrl })
			.then((results) => jiraFields?.complete(results.data?.jira.issuesByKey || []))
			.catch(logException)
			.finally(jiraFields?.finally);
	}
	// Fetch team names associated with the teamIds in Compass results
	if (teamIds.length > 0) {
		teamsBulk?.before?.();
		fetchTeamsBulk(
			{
				siteId: cloudId,
				teamIds,
			},
			logException,
		)
			.then((results) => teamsBulk?.complete(results.teams || []))
			.finally(teamsBulk?.finally);
	}
};

const useSetSearchResultsPostActions = () => {
	const [{ cloudId = '', aggAbsoluteUrl }] = useBootstrap();
	const {
		setJiraIssueDetails,
		setJiraIssueFieldsLoading,
		setCompassTeams,
		setCompassTeamsLoading,
	} = useSearchActions();
	const logException = useLogException();

	return useCallback(
		(aggResults: SearchPageResponse) =>
			fetchSearchResultsPostActions({
				results: aggResults.data?.search?.results,
				cloudId,
				aggAbsoluteUrl,
				jiraFields: {
					complete: setJiraIssueDetails,
					before: () => setJiraIssueFieldsLoading(true),
					finally: () => setJiraIssueFieldsLoading(false),
				},
				teamsBulk: {
					complete: setCompassTeams,
					before: () => setCompassTeamsLoading(true),
					finally: () => setCompassTeamsLoading(false),
				},
				logException,
			}),
		[
			cloudId,
			aggAbsoluteUrl,
			setJiraIssueDetails,
			setCompassTeams,
			setJiraIssueFieldsLoading,
			setCompassTeamsLoading,
			logException,
		],
	);
};

const useSetSearchResults = () => {
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const fetchFromCursor = useFetchFromCursor();
	const { setResults, appendMoreResults } = useSearchActions();
	const logException = useLogException();

	return useCallback(
		(aggResults: SearchPageResponse) => {
			const { data, extensions, errors } = aggResults;
			const errorMessage = errors?.[0]?.message;
			const resultsFetchError = errorMessage ? new ResultsFetchError(errorMessage) : undefined;
			if (fetchFromCursor && fetchFromCursor !== '') {
				appendMoreResults(data?.search?.results);
			} else {
				setResults(
					data?.search?.results,
					resultsFetchError,
					extensions?.gateway?.request_id || undefined,
					extensions?.gateway?.trace_id || undefined,
				);
			}
			if (resultsFetchError) {
				logException(resultsFetchError);
			}
		},
		[fetchFromCursor, setResults, appendMoreResults, logException],
	);
};

const useTrackIntercomEvents = () => {
	const intercom = useSafeIntercom();
	const [selectedProduct] = useSelectedProduct();
	return useCallback(() => {
		if (selectedProduct && is3pProductKey(selectedProduct)) {
			intercom?.trackEvent('search_requested_3p', { product_filter: selectedProduct });
		} else {
			intercom?.trackEvent('search_requested');
		}
	}, [selectedProduct, intercom]);
};

const useRestartUFOExperience = () => {
	const applicationMode = useApplicationMode();

	return useCallback(async () => {
		// resultsRef is nevert unset. Is this working as intended?
		// We should probably provide a way to check if the inlfight
		// promise is still active
		if (resultsRef.current) {
			await searchResultsLoadUFOExperience.abort({ metadata: { applicationMode } });
		}
		await searchResultsLoadUFOExperience.start();
		searchResultsLoadUFOExperience.mark('request_start');
	}, [applicationMode]);
};

const useSetLoadingFetchResults = () => {
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const fetchFromCursor = useFetchFromCursor();
	const { setIsLoadingMore, setIsLoading } = useSearchActions();

	return useCallback(() => {
		if (fetchFromCursor && fetchFromCursor !== '') {
			setIsLoadingMore(true);
		} else {
			setIsLoading(true);
		}
	}, [fetchFromCursor, setIsLoadingMore, setIsLoading]);
};

const useHandleFetchResultsError = () => {
	const { setResults } = useSearchActions();
	const { setProductResultCount } = useSearchActions();
	const trackIntercomEvents = useTrackIntercomEvents();

	return useCallback(
		(e: Error) => {
			if (e instanceof EmptyEntitiesError) {
				setResults(undefined, e);
			} else if (!(e instanceof CancelError)) {
				setResults(undefined, new ResultsFetchError(e.message));
				trackIntercomEvents();
			}
			setProductResultCount();
			return undefined;
		},
		[setResults, trackIntercomEvents, setProductResultCount],
	);
};

/**
 * Fetches search results using search parameters from the query
 * and filter stores. During and after requests, the results store is
 * updated accordingly. If multiple requests are fired only the last
 * request will cause the results store to be updated.
 */
export const useFetchResults = (): void => {
	const props = useFetchResultsProps();
	const {
		setQueryUpdatedTime,
		resetResults,
		setProductResultCount,
		setRequestedBackendExperiments,
	} = useSearchActions();

	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const isSearchReady = useIsSearchReady();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [retryCount] = useRetryCount();

	// Get current results and product count map for Slack special handling
	// we can add eslint disable rule here because this is not a relay code path and will be deprecated very soon once we fully move to relay
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [currentResults] = useResults();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [productResultCountMap] = useProductResultCountMap();
	const [selectedProduct] = useSelectedProduct();

	const fetchPostActions = useSetSearchResultsPostActions();
	const setSearchResults = useSetSearchResults();
	const setLoading = useSetLoadingFetchResults();
	const trackIntercomEvents = useTrackIntercomEvents();
	const restartUFOExperience = useRestartUFOExperience();
	const handleFetchResultsError = useHandleFetchResultsError();
	const logException = useLogException();

	const { onSearchResultsShown: onSearchResultsShownCallback } = useAppContext();

	const onSearchResultsShown = useCallback(
		(fetchedResults: SearchPageEdge[]) => {
			if (onSearchResultsShownCallback && fetchedResults.length > 0) {
				try {
					const results = transformSearchPageConfluenceResults(fetchedResults);
					if (results.length > 0) {
						onSearchResultsShownCallback(results);
					}
				} catch (error) {
					logException(error);
				}
			}
		},
		[onSearchResultsShownCallback, logException],
	);

	useEffect(() => {
		if (!isSearchReady) {
			return;
		}

		// Fetch search results
		const { searchQuery, selectedProduct } = props;
		fetchSearchResultsWithCount({
			...props,
			beforeFetch: () => {
				setProductResultCount();
				restartUFOExperience();
				setLoading();
				setQueryUpdatedTime();
				setRequestedBackendExperiments({
					experimentLayers: props.experimentLayers,
				});
			},
		})
			.then(([aggResults, aggResultsCount]) => {
				if (aggResults) {
					setSearchResults(aggResults);
					trackIntercomEvents();
					// Mark UFO experience loading times
					searchResultsLoadUFOExperience.mark('request_end');
					searchResultsLoadUFOExperience.mark('render_start');

					fetchPostActions(aggResults);
					onSearchResultsShown(aggResults.data?.search?.results?.edges || []);
				} else {
					resetResults();
				}

				setProductResultCount(
					aggResultsCount
						? getProductResultCount(searchQuery, selectedProduct, aggResultsCount)
						: undefined,
				);
			})
			.catch(handleFetchResultsError);
	}, [
		isSearchReady,
		setProductResultCount,
		restartUFOExperience,
		setLoading,
		setQueryUpdatedTime,
		fetchPostActions,
		handleFetchResultsError,
		resetResults,
		setSearchResults,
		trackIntercomEvents,
		setRequestedBackendExperiments,
		retryCount, // used to trigger a refetch
		props,
		onSearchResultsShown,
	]);

	// Special handling for Slack: Update count map with actual displayed results count
	useEffect(() => {
		const isSlackFederatedSearchEnabled =
			serveFederatedSlackExperiment(selectedProduct, undefined, { fireExperimentExposure: false }) ||
			fg('ai_3p_slack_federated_search_enabled');
		if (!isSlackFederatedSearchEnabled) {
			return;
		}

		if (selectedProduct === ProductKeys.Slack && currentResults?.edges && productResultCountMap) {
			const slackResultsCount = currentResults.edges.length;

			if (productResultCountMap[ProductKeys.Slack] !== slackResultsCount) {
				const updatedCounts: ProductCount[] = Object.entries({
					...productResultCountMap,
					[ProductKeys.Slack]: slackResultsCount,
				}).map(([product, count]) => ({ product, count }));

				setProductResultCount(updatedCounts);
			}
		}
	}, [selectedProduct, currentResults, productResultCountMap, setProductResultCount]);
};

export const useLeapQuery = () => {
	const [searchQuery] = useSearchQuery();
	const { setLeapQuery } = useSearchActions();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const { loading, candidates, titleIndex } = useLeapQueryResult();

	useEffect(() => {
		if (searchQuery === '') {
			setLeapQuery({
				candidates: [],
				titleIndex: 0,
				loading: false,
			});
		}
		setLeapQuery({
			candidates: [],
			titleIndex: 0,
			loading: true,
		});
		getLeapQuery({
			query: searchQuery,
			product: ProductKeys.Confluence,
		})
			.then((response) => {
				setLeapQuery({
					candidates: response.candidates,
					titleIndex: response.title,
					loading: false,
				});
			})
			.catch(() => {
				setLeapQuery({
					candidates: [],
					titleIndex: 0,
					loading: false,
				});
			});
	}, [searchQuery, setLeapQuery]);

	return {
		loading,
		candidates,
		titleIndex,
	};
};
